// Caret Positioning Utilities
// Pure functions for calculating and setting caret positions in contentEditable elements

/**
 * Calculates character offset from a click position in a contentEditable element
 * @param {HTMLElement} element - The contentEditable element
 * @param {number} clickX - X coordinate of the click
 * @param {number} clickY - Y coordinate of the click
 * @returns {number|null} - Character offset or null if unable to calculate
 */
export function calculateCaretOffsetFromClick(element, clickX, clickY) {
  let range = null;
  
  if (document.caretRangeFromPoint) {
    try {
      range = document.caretRangeFromPoint(clickX, clickY);
    } catch (err) {
      // Error getting caret range
    }
  } else if (document.caretPositionFromPoint) {
    try {
      const pos = document.caretPositionFromPoint(clickX, clickY);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    } catch (err) {
      // Error getting caret position
    }
  }
  
  if (range && element.contains(range.startContainer)) {
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    return preCaretRange.toString().length;
  }
  
  return null;
}

/**
 * Gets the current caret position in a contentEditable element
 * @param {HTMLElement} element - The contentEditable element
 * @returns {number} - Current caret position (0 if unable to determine)
 */
export function getCurrentCaretPosition(element) {
  const selection = window.getSelection();
  if (selection.rangeCount === 0) return 0;
  
  const range = selection.getRangeAt(0);
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(element);
  preCaretRange.setEnd(range.endContainer, range.endOffset);
  return preCaretRange.toString().length;
}

/**
 * Sets the caret position in a contentEditable element
 * @param {HTMLElement} element - The contentEditable element
 * @param {number} position - Target caret position
 * @returns {boolean} - True if position was set successfully
 */
export function setCaretPosition(element, position) {
  const textContent = element.textContent || '';
  const textLength = textContent.length;
  const clampedPosition = Math.min(position, textLength);
  
  if (textLength === 0) {
    const range = document.createRange();
    range.setStart(element, 0);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  }
  
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null
  );
  
  let currentOffset = 0;
  let textNode = walker.nextNode();
  let targetNode = null;
  let targetOffset = 0;
  
  while (textNode) {
    const nodeLength = textNode.textContent.length;
    if (currentOffset + nodeLength >= clampedPosition) {
      targetNode = textNode;
      targetOffset = clampedPosition - currentOffset;
      break;
    }
    currentOffset += nodeLength;
    textNode = walker.nextNode();
  }
  
  if (targetNode) {
    const range = document.createRange();
    range.setStart(targetNode, targetOffset);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    
    // Verify position
    if (selection.rangeCount > 0) {
      const verifyRange = document.createRange();
      verifyRange.selectNodeContents(element);
      verifyRange.setEnd(selection.getRangeAt(0).endContainer, selection.getRangeAt(0).endOffset);
      const actualPosition = verifyRange.toString().length;
      return actualPosition === clampedPosition;
    }
  }
  
  return false;
}

/**
 * Sets caret to the end of the element
 * @param {HTMLElement} element - The contentEditable element
 */
export function setCaretToEnd(element) {
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Sets caret to the start of the element
 * @param {HTMLElement} element - The contentEditable element
 */
export function setCaretToStart(element) {
  const range = document.createRange();
  range.setStart(element, 0);
  range.collapse(true);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}
